/**
 * Release Notes Configuration
 *
 * Human-written, user-facing summaries of what changed in each release. The
 * app renders this module on the "Wat is nieuw" page (`/release-notes`), linked
 * from the footer next to the running version.
 *
 * Per CLAUDE.md ("Release Notes Policy"): a new entry is written for every
 * version bump, just before the commit message, and committed together with the
 * change it describes. Entries are newest-first — add new releases at the top.
 *
 * Item `type` is one of `feature` (new functionality), `improvement` (better
 * behaviour of something that already existed) or `fix` (a corrected problem).
 * Labels for these types live in RELEASE_NOTE_TYPE_LABELS below so the page and
 * its tests share a single source of truth.
 */

/** Ordered item types and the Dutch heading each is rendered under. */
export const RELEASE_NOTE_TYPE_ORDER = ['feature', 'improvement', 'fix'];

/** Number of release entries shown per page on the "Wat is nieuw" page. */
export const RELEASE_NOTES_PER_PAGE = 5;

export const RELEASE_NOTE_TYPE_LABELS = {
  feature: 'Nieuwe functies',
  improvement: 'Verbeteringen',
  fix: 'Oplossingen'
};

/**
 * Release notes, newest-first. One object per released version.
 *
 * @type {Array<{ version: string, date: string, title: string, items: Array<{ type: 'feature'|'improvement'|'fix', text: string }> }>}
 */
export const RELEASE_NOTES = [
  {
    version: '0.3.22',
    date: '2026-10-13',
    title: 'Wat is nieuw in de app',
    items: [
      {
        type: 'feature',
        text: 'Nieuwe pagina "Wat is nieuw": een overzicht van de laatste wijzigingen, te bereiken via de link onderaan naast de versie.'
      }
    ]
  },
  {
    version: '0.3.21',
    date: '2026-09-30',
    title: 'Paneel van een gemeenschap of gastengroep komt direct in beeld',
    items: [
      {
        type: 'improvement',
        text: 'Wanneer u een paneel voor een gemeenschap of gastengroep opent, scrolt de pagina nu mee zodat u het paneel direct ziet.'
      }
    ]
  },
  {
    version: '0.3.20',
    date: '2026-09-25',
    title: 'Ledenlijst wordt in één keer opgeslagen',
    items: [
      {
        type: 'improvement',
        text: 'Het uploaden en bevestigen van een ledenlijst wordt nu als één geheel opgeslagen, zodat er geen verkeerde tussenstand meer kan ontstaan.'
      }
    ]
  },
  {
    version: '0.3.19',
    date: '2026-09-25',
    title: 'Download het ledenlijst-sjabloon al ingevuld',
    items: [
      {
        type: 'feature',
        text: 'De downloadknop vult het ledenlijst-sjabloon nu al in met de leden die er al staan, en zet de volledige naam (bisdom, parochie, gemeenschap) in de bestandsnaam.'
      }
    ]
  },
  {
    version: '0.3.18',
    date: '2026-09-24',
    title: 'Bevestigen van deelname is robuuster',
    items: [
      {
        type: 'improvement',
        text: 'Het bevestigen van deelname probeert tijdelijke verbindingsproblemen nu automatisch opnieuw en voorkomt dat u per ongeluk twee keer bevestigt.'
      }
    ]
  },
  {
    version: '0.3.17',
    date: '2026-09-24',
    title: 'Duidelijkere foutmeldingen',
    items: [
      {
        type: 'improvement',
        text: 'Foutmeldingen zijn duidelijker en bevatten nu een code die u aan de helpdesk kunt doorgeven.'
      }
    ]
  },
  {
    version: '0.3.16',
    date: '2026-09-23',
    title: 'Wie heeft bevestigd staat in de melding',
    items: [
      {
        type: 'improvement',
        text: 'De melding bij een bevestigde deelname vermeldt nu wie de deelname heeft bevestigd.'
      }
    ]
  },
  {
    version: '0.3.15',
    date: '2026-09-22',
    title: 'Etappes blijven langer zichtbaar en de geopende knop is herkenbaar',
    items: [
      {
        type: 'improvement',
        text: 'Een etappe blijft nu 4 weken na de datum zichtbaar, zodat u recente catecheses kunt nateraden.'
      },
      {
        type: 'improvement',
        text: 'De knop waarvan het paneel open staat (Bevestigen of Ledenlijst) krijgt nu een ingedrukte look.'
      }
    ]
  },
  {
    version: '0.3.14',
    date: '2026-09-22',
    title: 'Leden bewerken wist de bevestiging niet meer',
    items: [
      {
        type: 'improvement',
        text: 'Wanneer u een lid handmatig toevoegt, bewerkt of verwijdert in het Leden-tabblad, blijft de bestaande bevestiging bewaard: bevestigde leden houden hun aankomst- en vertrekdatum.'
      }
    ]
  },
  {
    version: '0.3.13',
    date: '2026-09-21',
    title: 'Ledenlijsten uit de vorige opzet laden correct',
    items: [
      {
        type: 'fix',
        text: 'Conviventies die nog uit de vorige opzet kwamen, tonen hun ledenlijst nu correct in plaats van een lege lijst.'
      }
    ]
  },
  {
    version: '0.3.12',
    date: '2026-09-21',
    title: 'Naamkolommen in de juiste volgorde',
    items: [
      {
        type: 'fix',
        text: 'In het bevestigpaneel staat Voornaam nu weer voor Achternaam, net als in de overige overzichten.'
      }
    ]
  },
  {
    version: '0.3.11',
    date: '2026-09-21',
    title: 'Het Leden-tabblad is terug',
    items: [
      {
        type: 'feature',
        text: 'Met het tabblad "Leden" kunt u een geladen ledenlijst handmatig aanvullen: leden toevoegen, bewerken of verwijderen zonder het hele bestand opnieuw te uploaden.'
      },
      {
        type: 'improvement',
        text: 'Het uploaden van een ledenlijst gebeurt nu op één plek; de conviventie-kaart leidt u via een link naar het Leden-tabblad.'
      }
    ]
  },
  {
    version: '0.3.6',
    date: '2026-09-13',
    title: 'De overstap naar de nieuwe leden-upload is voltooid',
    items: [
      {
        type: 'improvement',
        text: 'Leden worden nu per conviventie beheerd via de upload; de oude permanente ledenlijst is vervallen en er is hulpmiddel toegevoegd om bestaande leden over te zetten.'
      }
    ]
  },
  {
    version: '0.3.3',
    date: '2026-09-13',
    title: 'Ledenlijsten per conviventie via Excel',
    items: [
      {
        type: 'feature',
        text: 'U kunt een ledenlijst per conviventie via Excel uploaden en daarna de deelname bevestigen. De lijst vervalt automatisch na de gebeurtenis.'
      },
      {
        type: 'improvement',
        text: 'De einddatum van een conviventie is verplicht en bepaalt hoelang de ledenlijst bewaard blijft.'
      }
    ]
  },
  {
    version: '0.3.1',
    date: '2026-09-13',
    title: 'Betrouwbaardere verwerking van Excel-bestanden',
    items: [
      {
        type: 'improvement',
        text: 'De verwerking van Excel-bestanden is op een nieuwere, beter onderhouden basis gezet, als voorbereiding op de nieuwe leden-upload.'
      }
    ]
  },
  {
    version: '0.2.27',
    date: '2026-08-29',
    title: 'Persoonlijk overzicht',
    items: [
      {
        type: 'feature',
        text: 'Nieuwe pagina "Mijn overzicht" met uw profiel (naam en telefoon aanpasbaar) en de gemeenschappen waarvoor u als verantwoordelijke of catechist bent toegewezen.'
      }
    ]
  },
  {
    version: '0.2.26',
    date: '2026-08-29',
    title: 'Deelbare links naar een gemeenschap of gastengroep',
    items: [
      {
        type: 'improvement',
        text: 'U kunt nu een directe link openen naar een specifieke gemeenschap of gastengroep in het Leden-overzicht.'
      }
    ]
  },
  {
    version: '0.2.25',
    date: '2026-08-27',
    title: 'Anonieme gebruiksstatistieken',
    items: [
      {
        type: 'improvement',
        text: 'Er worden anonieme gebruiksgegevens verzameld om de app te kunnen verbeteren. Er worden geen namen of e-mailadressen bijgehouden.'
      }
    ]
  },
  {
    version: '0.2.24',
    date: '2026-08-27',
    title: 'Snelkoppelingen voor aankomst- en vertrekdatum',
    items: [
      {
        type: 'improvement',
        text: 'In het bevestigpaneel kunt u de aankomst- en vertrekdatum nu met één klik invullen met de datums van de conviventie.'
      }
    ]
  },
  {
    version: '0.2.23',
    date: '2026-08-27',
    title: 'Alle parochies zijn te kiezen',
    items: [
      {
        type: 'fix',
        text: 'Parochies zonder bestaande gemeenschappen worden nu wel getoond in de keuzelijst bij het aanmaken van een etappe.'
      }
    ]
  },
  {
    version: '0.2.22',
    date: '2026-08-23',
    title: 'Duidelijke meldingen bij lege lijsten',
    items: [
      {
        type: 'improvement',
        text: 'Lege overzichten (gemeenschappen, leden, conviventies, catecheses en gebruikers) tonen nu een duidelijke uitleg in plaats van een leeg scherm.'
      }
    ]
  },
  {
    version: '0.2.21',
    date: '2026-08-23',
    title: 'Gastengroepen beheren en verwijderen',
    items: [
      {
        type: 'feature',
        text: 'Beheerders kunnen gastengroepen nu verwijderen — met een waarschuwing als de groep nog door een aankomende conviventie wordt gebruikt — en de leden van een gastengroep beheren.'
      }
    ]
  },
  {
    version: '0.2.20',
    date: '2026-08-15',
    title: 'Duidelijker bevestigpaneel',
    items: [
      {
        type: 'improvement',
        text: 'Het bevestigpaneel toont nu een duidelijke melding als een gemeenschap of gastengroep nog geen leden heeft, en de aankomst- en vertrekdatum starten leeg.'
      }
    ]
  },
  {
    version: '0.2.19',
    date: '2026-08-14',
    title: 'Gastengroepen',
    items: [
      {
        type: 'feature',
        text: 'Naast gemeenschappen kunt u nu ook losse gastengroepen uitnodigen en hun leden beheren. Beheerders kunnen gastengroepen toevoegen en hun leden onderhouden.'
      }
    ]
  },
  {
    version: '0.2.18',
    date: '2026-08-13',
    title: 'Gemakkelijker gemeenschap kiezen en beter leesbaar op mobiel',
    items: [
      {
        type: 'improvement',
        text: 'Een gemeenschap kiezen gaat nu stap voor stap (bisdom, dan parochie, dan gemeenschap), en het ledenoverzicht is overzichtelijker en beter leesbaar op een telefoon.'
      }
    ]
  },
  {
    version: '0.2.17',
    date: '2026-08-11',
    title: 'Parochie-namen gelijkgetrokken',
    items: [
      {
        type: 'improvement',
        text: 'De schrijfwijze van parochie-namen is overal in de app gelijkgetrokken.'
      }
    ]
  },
  {
    version: '0.2.16',
    date: '2026-08-11',
    title: 'Melding bij bevestigde deelname',
    items: [
      {
        type: 'feature',
        text: 'Wanneer een gemeenschap haar deelname bevestigt, ontvangen geabonneerde beheerders een melding met de naam van de conviventie en het aantal bevestigde leden.'
      },
      {
        type: 'improvement',
        text: 'Het aantal leden wordt nu per status geteld: een echtpaar telt als twee personen.'
      }
    ]
  },
  {
    version: '0.2.15',
    date: '2026-08-11',
    title: 'Aantal leden wordt automatisch bijgewerkt',
    items: [
      {
        type: 'improvement',
        text: 'Het aantal leden van een gemeenschap wordt nu automatisch opnieuw berekend zodra er een lid wordt toegevoegd, gewijzigd of verwijderd.'
      }
    ]
  },
  {
    version: '0.2.14',
    date: '2026-08-08',
    title: 'Catechisten en duidelijkere rechten',
    items: [
      {
        type: 'feature',
        text: 'Catechisten kunnen nu de etappes van hun eigen gemeenschap beheren.'
      },
      {
        type: 'improvement',
        text: 'De toegangsrechten zijn gestandaardiseerd op één model: wie toegang heeft tot een gemeenschap, kan de bijbehorende etappes beheren.'
      }
    ]
  },
  {
    version: '0.2.13',
    date: '2026-08-08',
    title: 'Toegang en verantwoordelijke-gegevens op orde',
    items: [
      {
        type: 'improvement',
        text: 'De manier waarop wordt bepaald wie toegang heeft tot een gemeenschap is versneld en gestandaardiseerd; bestaande gegevens van verantwoordelijken zijn automatisch overgezet naar het nieuwe model.'
      }
    ]
  },
  {
    version: '0.2.12',
    date: '2026-08-08',
    title: 'Verantwoordelijke met aparte naamvelden',
    items: [
      {
        type: 'improvement',
        text: 'De verantwoordelijke wordt nu opgeslagen met aparte velden voor voornaam en achternaam, samen met het e-mailadres.'
      }
    ]
  },
  {
    version: '0.2.11',
    date: '2026-08-05',
    title: 'Uitgebreidere export en hulpmiddelen voor importeren',
    items: [
      {
        type: 'improvement',
        text: 'De export van conviventies is uitgebreid, en er zijn hulpmiddelen toegevoegd voor het importeren en exporteren van leden en gemeenschappen.'
      }
    ]
  },
  {
    version: '0.2.10',
    date: '2026-08-01',
    title: 'Uitgebreider lid-formulier',
    items: [
      {
        type: 'improvement',
        text: 'Het formulier om een lid te bewerken bevat nu meer velden.'
      }
    ]
  },
  {
    version: '0.2.9',
    date: '2026-08-01',
    title: 'Stevigere rechten op conviventies',
    items: [
      {
        type: 'fix',
        text: 'De regels voor wie een conviventie mag inzien zijn aangescherpt, met een veilige terugval voor oudere gegevens.'
      }
    ]
  },
  {
    version: '0.2.7',
    date: '2026-07-26',
    title: 'Juiste conviventies voor de juiste gemeenschap',
    items: [
      {
        type: 'fix',
        text: 'Conviventies worden nu alleen getoond aan de gemeenschappen die zijn uitgenodigd of die er verantwoordelijk voor zijn.'
      }
    ]
  },
  {
    version: '0.2.6',
    date: '2026-07-25',
    title: 'Mobiel menu en nieuwe naam voor etappes',
    items: [
      {
        type: 'feature',
        text: 'De app heeft nu een hamburgermenu op kleinere schermen, terwijl de tabs op grotere schermen behouden blijven.'
      },
      {
        type: 'improvement',
        text: 'De knop "Nieuwe catechese" heet nu "Nieuwe etappe".'
      }
    ]
  },
  {
    version: '0.2.5',
    date: '2026-07-23',
    title: 'Locatie kiezen en suggestie voor de volgende etappe',
    items: [
      {
        type: 'improvement',
        text: 'Bij een etappe kiest u eerst bisdom, parochie en gemeenschap, en de app stelt op basis van de laatste etappe een volgende stap voor.'
      }
    ]
  },
  {
    version: '0.2.4',
    date: '2026-07-23',
    title: 'Naam invullen bij registratie',
    items: [
      {
        type: 'feature',
        text: 'Bij het registreren vult u nu uw voornaam en achternaam in; deze worden in uw profiel opgeslagen.'
      }
    ]
  },
  {
    version: '0.2.3',
    date: '2026-07-20',
    title: 'Help-toelichtingen per pagina',
    items: [
      {
        type: 'improvement',
        text: 'Elke pagina heeft nu een helpknop (?) met uitleg over het doel en het gebruik.'
      }
    ]
  },
  {
    version: '0.2.2',
    date: '2026-07-20',
    title: 'Duidelijkere uitnodigingen',
    items: [
      {
        type: 'improvement',
        text: 'Bij het uitnodigen van gemeenschappen voor een conviventie worden parochie en naam duidelijker weergegeven.'
      }
    ]
  },
  {
    version: '0.2.1',
    date: '2026-07-20',
    title: 'Uitnodigingsvenster verfijnd',
    items: [
      {
        type: 'improvement',
        text: 'Het keuzevakje om een gemeenschap uit te nodigen staat nu aan de linkerkant, en de labels blijven netjes op één regel.'
      }
    ]
  },
  {
    version: '0.1.1',
    date: '2026-07-19',
    title: 'Conviventies met bevestiging en export',
    items: [
      {
        type: 'feature',
        text: 'Nieuwe conviventies-module: overzichtskaarten, een aanmaak- en bewerkingsvenster, en het bevestigen van leden rechtstreeks in de kaart met een aankomst- en vertrekdatum per lid.'
      },
      {
        type: 'feature',
        text: 'Conviventies en leden kunnen nu naar Excel worden geëxporteerd.'
      }
    ]
  },
  {
    version: '0.1.0',
    date: '2026-05-13',
    title: 'Eerste versie',
    items: [
      {
        type: 'feature',
        text: 'Eerste versie van de app, met de basis voor het beheren van catechese-etappes en gemeenschappen.'
      }
    ]
  }
];

/**
 * Returns the release-notes entry matching the running app version.
 *
 * @param {string} [version] - The version to look up (e.g. process.env.REACT_APP_VERSION).
 * @returns {object|undefined} The matching entry, or undefined when none matches.
 */
export const getCurrentReleaseNote = (version) => (
  RELEASE_NOTES.find((entry) => entry.version === version)
);

/** @returns {object|undefined} The newest release-notes entry. */
export const getLatestReleaseNote = () => RELEASE_NOTES[0];
